/* eslint-disable import/no-unresolved */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS } from '@contentful/rich-text-types';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

import Container from '../../../Core/Container';
import RichText, { fragment } from '../../../Core/RichText';
import Link from '../../../Core/Link';
import IndividualBlade from '../../IndividualBlades';
import Select from '../../../Core/Select';
import Carousel from '../../../Core/Carousel';

import {
  Heading,
  ContentWrapper,
  Footer,
  StyledCard,
  FilterContainer,
  OverflowContainer,
  OverflowHeaderContainer,
  HeaderWrapper,
  StyledImage,
  Subheading,
} from './styles';

const cleanString = (str, reverse = false) => {
  return !reverse ? str.split(' ').join('-') : str.split('-').join(' ');
};

const createFilterOption = ({ value, label }) => {
  return {
    value: cleanString(value),
    label: ReactHtmlParser(label),
    ariaLabel: label,
  };
};

const createFilterOptions = options =>
  options
    ? options.map(option =>
        createFilterOption({
          value: option.value,
          label: option.label,
          ariaLabel: option.ariaLabel,
        }),
      )
    : null;

const OverflowCarousel = ({ data }) => {
  if (!data) return null;

  const {
    anchorName,
    backgroundColor,
    contentAlignment,
    elements,
    sectionContainerHeading,
    sectionContainerHeadingColor,
    sectionSubheading,
    ctas,
    imageDam,
  } = data;

  const elementHeaders = elements.map(el => {
    return {
      value: el.id,
      label: documentToHtmlString(JSON.parse(el.sectionContainerHeading.raw)),
    };
  });

  const defaultFilterSelection = createFilterOption(elementHeaders[0]);
  const filterSelections = createFilterOptions(elementHeaders);

  // state management for active card selection, dropdown filter, and carousel
  const [activeCard, setActiveCard] = useState(elements[0]);
  const [activeFilter, setActiveFilter] = useState(defaultFilterSelection);

  const setCard = cardId => {
    const selectedCard = elements.find(el => el.id === cardId);
    setActiveCard(selectedCard);
  };

  const numOfItems = activeCard.elements.length;

  const breakpoints = {
    320: {
      slidesPerView: '1',
      spaceBetween: 24,
    },
    768: {
      slidesPerView: '2.25',
      spaceBetween: 24,
    },
    1024: {
      slidesPerView: numOfItems > 3 ? 3.25 : 3,
      spaceBetween: 24,
    },
  };

  useEffect(() => {
    setCard(activeFilter.value);
  }, [activeFilter]);

  const header = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Heading
          contentAlignment={contentAlignment}
          headingColor={sectionContainerHeadingColor}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (_, children) => (
        <Heading
          contentAlignment={contentAlignment}
          headingColor={sectionContainerHeadingColor}
        >
          {children}
        </Heading>
      ),
    },
  };

  const subheader = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <Subheading>{children}</Subheading>,
    },
  };

  return (
    <OverflowContainer>
      <Container
        backgroundColor={backgroundColor}
        as="section"
        id={anchorName}
        className="auto-margin"
      >
        <OverflowHeaderContainer>
          <HeaderWrapper hasImg={!!imageDam}>
            {sectionContainerHeading && (
              <RichText document={sectionContainerHeading} options={header} />
            )}
            {sectionSubheading && (
              <RichText document={sectionSubheading} options={subheader} />
            )}
            {elements.length > 1 && (
              <FilterContainer>
                <Select
                  className="overflow-carousel-filter"
                  options={[...filterSelections]}
                  state={activeFilter}
                  setState={setActiveFilter}
                  // the label is an id pulled from the locales files which gets passed to the <label> in the Select component
                  label="creditCard"
                />
              </FilterContainer>
            )}
          </HeaderWrapper>
          {imageDam && (
            <StyledImage src={imageDam} alt={imageDam.altText || ''} />
          )}
        </OverflowHeaderContainer>
        <ContentWrapper className={contentAlignment}>
          {elements.map(el =>
            el.id === activeCard.id ? (
              <Carousel
                key={el.id}
                breakpoints={breakpoints}
                modules={[Pagination]}
                pagination={{ clickable: true }}
              >
                {activeCard.elements.map(card => {
                  return (
                    <SwiperSlide key={card.id}>
                      <StyledCard
                        data={card}
                        className="carousel-cards"
                        forwardedAs="div"
                      />
                    </SwiperSlide>
                  );
                })}
              </Carousel>
            ) : null,
          )}
          {ctas && (
            <Footer>
              {ctas.map(cta => {
                if (cta.hideFrench === true) return null;
                return cta.internal.type === 'ContentfulBlade' ? (
                  <IndividualBlade key={cta.id} data={cta} />
                ) : (
                  <Link key={cta.id} data={cta}>
                    <RichText document={cta.label} options={fragment} />
                  </Link>
                );
              })}
            </Footer>
          )}
        </ContentWrapper>
      </Container>
    </OverflowContainer>
  );
};

OverflowCarousel.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default OverflowCarousel;
